
import Vue from 'vue';
import Component from "vue-class-component";
import HeadingWave from "@/features/shared/components/HeadingWave.vue";
import {
    AddOrUpdateShipRequest,
    CountriesClient,
    CountryLightModel,
    factory, ResultError, ShipDetailsModel,
    ShipTypeLocalizedModel,
    ShipTypesClient, SysAdminShipsClient,
} from "@/api-client";
import toasts from "@/components/Toasts";
import LabelOptional from "@/features/shared/components/LabelOptional.vue";
import AdministrationDetailviewLayout from "@/features/administration/AdministrationDetailviewLayout.vue";
import {Prop} from "vue-property-decorator";
import {NavigationGuardNext} from "vue-router/types/router";
import ErrorsSummary from "@/components/ErrorsSummary.vue";
import DropdownSearch from "@/components/DropdownSearch.vue";

interface Props {
    model?: AddOrUpdateShipRequest;
    isActive?: boolean;
    shipTypeOptions: ShipTypeLocalizedModel[];
    countryOptions: CountryLightModel[];
}

@Component({
    components: {
        ErrorsSummary,
        HeadingWave,
        LabelOptional,
        DropdownSearch,
        AdministrationDetailviewLayout
    },
    beforeRouteEnter(to, from, next: NavigationGuardNext<UpsertShip>) {
        const props = to.meta!.props as Props;
        Promise.all([
            new ShipTypesClient().shipTypesLocalized(""),
            new CountriesClient().countries(),
            to.params.id ? new SysAdminShipsClient().details(to.params.id) : Promise.resolve(null)
        ]).then(values => {
            props.shipTypeOptions = values[0].shipTypesLocalized.sort((a,b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0));
            props.countryOptions = values[1];
            const shipToUpdate: ShipDetailsModel | null = values[2];

            if (shipToUpdate) {
                props.model = new AddOrUpdateShipRequest({
                    name: shipToUpdate.name,
                    type: shipToUpdate.type,
                    imoNr: shipToUpdate.imoNr,
                    call: shipToUpdate.call,
                    mmsi: shipToUpdate.mmsi,
                    countryCode: shipToUpdate.countryCode,
                    grossTonnage: shipToUpdate.grossTonnage,
                    mainPower: shipToUpdate.mainPower,
                    electricPower: shipToUpdate.electricPower,
                    coldProduction: shipToUpdate.coldProduction,
                    additionalInfo: shipToUpdate.additionalInfo,
                    lengthOverall: shipToUpdate.lengthOverall
                });
                props.isActive = shipToUpdate.isActive;
            }
            next();
        }).catch(e => next(e))
    }
})
export default class UpsertShip extends Vue implements Props {
    @Prop({required: false, default: () => factory.create(AddOrUpdateShipRequest)})
    model!: AddOrUpdateShipRequest;

    @Prop({required: false, default: () => true})
    isActive!: boolean;

    @Prop({required: true})
    shipTypeOptions!: ShipTypeLocalizedModel[];

    @Prop({required: true})
    countryOptions!: CountryLightModel[];

    validationErrors: ResultError = ResultError.createEmpty();

    get isEditMode(): boolean {
        return this.$route.params.id != null;
    }

    get isActiveLabel(): string {
        return `${this.$ls.Administration_Ships_CurrentShipActivity} ${this.isActive ? this.$ls.Global_Label_Active : this.$ls.Global_Label_InActive}`
    }

    get activationButtonText(): string {
        return this.isActive ? this.$ls.Administration_Ships_Deactivate_Action : this.$ls.Administration_Ships_Activate_Action;
    }

    get id(): string | null {
        return this.$route.params.id;
    }

    async save() {
        this.validationErrors.clear();

        if (this.isEditMode) {
            this.model.lengthOverall = Number(this.model.lengthOverall?.toString().replace(",", ".")) ?? null;
            await new SysAdminShipsClient().update(this.$route.params.id, this.model)
                .then(
                    () => {
                        this.$router.push({name: 'ships'});
                        toasts.success(`${this.$ls.Administration_Ships_Toasts_Update_Success}: ${this.model.name}`);
                    },
                    (errorResponse: ResultError) => {
                        this.validationErrors = errorResponse;
                        toasts.failure(this.$ls.Global_Toast_ActionFailureWithErrorsAbove);
                    });
        } else {
            await new SysAdminShipsClient().add(this.model)
                .then(
                    () => {
                        this.$router.push({name: 'ships'});
                        toasts.success(`${this.$ls.Administration_Ships_Toasts_Adding_Success}: ${this.model.name}`);
                    },
                    (errorResponse: ResultError) => {
                        this.validationErrors = errorResponse;
                        toasts.failure(this.$ls.Global_Toast_ActionFailureWithErrorsAbove);
                    });
        }
    }

    async changeActiveState() {
        this.validationErrors.clear();

        if (this.isActive) {
            await new SysAdminShipsClient().deactivate(this.$route.params.id)
                .then(
                    () => {
                        this.$router.push({name: 'ships'});
                        toasts.success(`${this.$ls.Administration_Ships_Toasts_Deactivation_Success}: ${this.model.name}`);
                    },
                    (errorResponse: ResultError) => {
                        this.validationErrors = errorResponse;
                        toasts.failure(this.$ls.Global_Toast_ActionFailureWithErrorsAbove);
                    });
        } else {
            await new SysAdminShipsClient().activate(this.$route.params.id)
                .then(
                    () => {
                        this.$router.push({name: 'ships'});
                        toasts.success(`${this.$ls.Administration_Ships_Toasts_Activation_Success}: ${this.model.name}`);
                    },
                    (errorResponse: ResultError) => {
                        this.validationErrors = errorResponse;
                        toasts.failure(this.$ls.Global_Toast_ActionFailureWithErrorsAbove);
                    });
        }
    }

    formatLength (value: string): string | null {
        if (value.length === 0) return null;
        value = value.replace(",", ".");
        if (isNaN(Number(value)))
            return null;
        return Number(value.replace(",", ".")).toFixed(2).toString().replace(".", ",");
    }
}
